import { useState, useEffect, useCallback } from 'react';

// Types
export interface Package {
  id: string;
  name: string;
  badge: string;
  description: string;
  features: string[];
  link: string;
  color: string;
  popular: boolean;
  active: boolean;
  order: number;
}

export interface Hotel {
  id: string;
  stars: number;
  label: string;
  badge: string;
  images: string[];
  active: boolean;
  order: number;
}

export interface Bus {
  id: string;
  type: string;
  seats: string;
  images: string[];
  active: boolean;
  order: number;
}

export interface Testimonial {
  id: string;
  name: string;
  text: string;
  rating: number;
  date: string;
  active: boolean;
}

export interface SiteSettings {
  siteName: string;
  siteNameEn: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  city: string;
  country: string;
  heroTitle: string;
  heroSubtitle: string;
  heroCta: string;
  aboutTitle: string;
  aboutDescription: string;
  primaryColor: string;
  secondaryColor: string;
}

export interface CMSData {
  packages: Package[];
  hotels: Hotel[];
  buses: Bus[];
  testimonials: Testimonial[];
  settings: SiteSettings;
  lastUpdated: string;
  version: number;
}

// Default data
const defaultPackages: Package[] = [
  {
    id: 'pkg-1',
    name: 'الباقة الاقتصادية',
    badge: 'متاحة يومياً تقريباً',
    description: 'رحلات عمرة من الرياض بسعر مناسب دون التنازل عن الراحة.',
    features: [
      'باص حديث موديل 2027 — 4 صفوف، 49 مقعداً',
      'فندق 3 نجوم',
      'مكة فقط، أو مكة والمدينة',
      'مدد مرنة: 3 أو 5 أيام أو أكثر',
      'غرف عائلية خاصة أو مشتركة للعزاب',
    ],
    link: 'https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن الباقة الاقتصادية لرحلة عمرة من الرياض',
    color: 'brown',
    popular: false,
    active: true,
    order: 1,
  },
  {
    id: 'pkg-2',
    name: 'باقة VIP الفاخرة',
    badge: 'الأكثر تميزاً',
    description: 'تجربة راقية لرحلة عمرة VIP من الرياض بمستوى خدمة أعلى.',
    features: [
      'باص فاخر — 3 صفوف، 30 مقعداً فقط',
      'فندق 4 أو 5 نجوم',
      'كل اثنين وخميس أسبوعياً',
      '3 أيام في مكة المكرمة',
      'للعزاب والعائلات',
    ],
    link: 'https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن باقة VIP لرحلة عمرة من الرياض',
    color: 'gold',
    popular: true,
    active: true,
    order: 2,
  },
  {
    id: 'pkg-3',
    name: 'حجز مقاعد الباص',
    badge: 'مرونة كاملة',
    description: 'مقاعد باصات العمرة من الرياض إلى مكة دون سكن — بمرونة تامة.',
    features: [
      'ذهاب وعودة أو اتجاه واحد',
      'الرياض → مكة أو مكة → الرياض',
      'باصات حديثة موديل 2027',
      'مواعيد منتظمة',
      'مناسب لمن دبّر سكنه بنفسه',
    ],
    link: 'https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن حجز مقعد في باص العمرة من الرياض',
    color: 'navy',
    popular: false,
    active: true,
    order: 3,
  },
];

const defaultHotels: Hotel[] = [
  {
    id: 'hotel-1',
    stars: 3,
    label: 'فنادق 3 نجوم',
    badge: 'الباقة الاقتصادية',
    images: [
      'https://doratalhuda.com/storage/uploads/gallery/dtr9xpYWsFJCZzSQD7ml.webp',
      'https://doratalhuda.com/storage/uploads/gallery/TbVWcu5YNyzBOCxdieNW.webp',
      'https://doratalhuda.com/storage/uploads/gallery/O1FqjDRzfgp6l4JKcKrw.webp',
      'https://doratalhuda.com/storage/uploads/gallery/p3vFVnMcS0a3SaZgYjl4.webp',
    ],
    active: true,
    order: 1,
  },
  {
    id: 'hotel-2',
    stars: 5,
    label: 'فنادق 5 نجوم',
    badge: 'باقة VIP',
    images: [
      'https://doratalhuda.com/storage/uploads/gallery/nIuVSbHtCL95yCf4cJ79.webp',
      'https://doratalhuda.com/storage/uploads/gallery/DWKK7ievIdqtujnhJCR5.webp',
      'https://doratalhuda.com/storage/uploads/gallery/RDcIR1IuwZfz4ZKtB2Vx.webp',
      'https://doratalhuda.com/storage/uploads/gallery/xLbYVQn6YKFHjtaiLd9b.webp',
    ],
    active: true,
    order: 2,
  },
];

const defaultBuses: Bus[] = [
  {
    id: 'bus-1',
    type: 'اقتصادي',
    seats: '49 مقعد — مريح وسعره مناسب',
    images: [
      'https://doratalhuda.com/storage/uploads/gallery/IfE6BElTu5OLRXywRmnx.webp',
      'https://doratalhuda.com/storage/uploads/gallery/oEg78DqXPfFGbBJgxwCF.webp',
      'https://doratalhuda.com/storage/uploads/gallery/CvUvK0UKIPLpQMAWikDT.webp',
      'https://doratalhuda.com/storage/uploads/gallery/7kiklWkOYrrUFJE1ZFsV.webp',
    ],
    active: true,
    order: 1,
  },
  {
    id: 'bus-2',
    type: 'VIP',
    seats: '30 مقعد فقط — 3 صفوف فاخرة',
    images: [
      'https://doratalhuda.com/storage/uploads/gallery/G1yQreLvNoXXIKhHlrgc.webp',
      'https://doratalhuda.com/storage/uploads/gallery/SZWWHegI4X1ZUuwdSIiG.webp',
      'https://doratalhuda.com/storage/uploads/gallery/9GpROnRqJdGlWN7zsJDJ.webp',
      'https://doratalhuda.com/storage/uploads/gallery/h9vJr52TJY5Dg8HVyA9M.webp',
    ],
    active: true,
    order: 2,
  },
];

const defaultTestimonials: Testimonial[] = [
  {
    id: 'test-1',
    name: 'عبدالله محمد',
    text: 'رحلة ممتازة والباص كان نظيف وال staff محترم جداً. أنصح بالباقة الاقتصادية.',
    rating: 5,
    date: '2024-01-15',
    active: true,
  },
  {
    id: 'test-2',
    name: 'فاطمة أحمد',
    text: 'التجربة كانت رائعة مع باقة VIP، الفندق ممتاز والخدمة فاخرة.',
    rating: 5,
    date: '2024-02-20',
    active: true,
  },
  {
    id: 'test-3',
    name: 'محمد العلي',
    text: 'أسعار مناسبة وخدمة ممتازة. حجزت مقعد الباص وكانت الرحلة مريحة.',
    rating: 4,
    date: '2024-03-10',
    active: true,
  },
];

const defaultSettings: SiteSettings = {
  siteName: 'سدرة المنتهى',
  siteNameEn: 'Sudra Al Muntaha',
  phone: '0560856311',
  whatsapp: '966575179602',
  email: 'info@sudra-al-muntaha.com',
  address: 'الرياض، المملكة العربية السعودية',
  city: 'الرياض',
  country: 'المملكة العربية السعودية',
  heroTitle: 'حملات عمرة من الرياض',
  heroSubtitle: 'رحلات منظمة إلى مكة والمدينة — باصات حديثة وفنادق مريحة',
  heroCta: 'احجز رحلتك الآن',
  aboutTitle: 'عن سدرة المنتهى',
  aboutDescription: 'شركة متخصصة في حملات العمرة من الرياض إلى مكة والمدينة المنورة',
  primaryColor: '#8B4513',
  secondaryColor: '#DAA520',
};

// Storage key
const CMS_STORAGE_KEY = 'sudra-cms-data';
const CMS_PASSWORD_KEY = 'sudra-cms-password';
const CMS_AUTH_KEY = 'sudra-cms-auth';

// Generate unique ID
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get stored data or defaults
const getStoredData = (): CMSData => {
  try {
    const stored = localStorage.getItem(CMS_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Merge with defaults for any missing fields
      return {
        packages: data.packages || defaultPackages,
        hotels: data.hotels || defaultHotels,
        buses: data.buses || defaultBuses,
        testimonials: data.testimonials || defaultTestimonials,
        settings: { ...defaultSettings, ...data.settings },
        lastUpdated: data.lastUpdated || new Date().toISOString(),
        version: (data.version || 0) + 1,
      };
    }
  } catch (error) {
    console.error('Error loading CMS data:', error);
  }
  return {
    packages: defaultPackages,
    hotels: defaultHotels,
    buses: defaultBuses,
    testimonials: defaultTestimonials,
    settings: defaultSettings,
    lastUpdated: new Date().toISOString(),
    version: 1,
  };
};

// Save data to storage
const saveData = (data: CMSData): void => {
  try {
    data.lastUpdated = new Date().toISOString();
    data.version += 1;
    localStorage.setItem(CMS_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving CMS data:', error);
  }
};

// Custom hook for CMS data
export const useCMS = () => {
  const [data, setData] = useState<CMSData>(getStoredData);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setData(getStoredData());
    setIsLoading(false);
  }, []);

  const updateData = useCallback((newData: Partial<CMSData>) => {
    setData((prev) => {
      const updated = { ...prev, ...newData };
      saveData(updated);
      return updated;
    });
  }, []);

  // Packages
  const addPackage = useCallback((pkg: Omit<Package, 'id'>) => {
    const newPkg: Package = { ...pkg, id: generateId() };
    setData((prev) => {
      const updated = {
        ...prev,
        packages: [...prev.packages, newPkg],
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const updatePackage = useCallback((id: string, updates: Partial<Package>) => {
    setData((prev) => {
      const updated = {
        ...prev,
        packages: prev.packages.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        ),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const deletePackage = useCallback((id: string) => {
    setData((prev) => {
      const updated = {
        ...prev,
        packages: prev.packages.filter((p) => p.id !== id),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  // Hotels
  const addHotel = useCallback((hotel: Omit<Hotel, 'id'>) => {
    const newHotel: Hotel = { ...hotel, id: generateId() };
    setData((prev) => {
      const updated = {
        ...prev,
        hotels: [...prev.hotels, newHotel],
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const updateHotel = useCallback((id: string, updates: Partial<Hotel>) => {
    setData((prev) => {
      const updated = {
        ...prev,
        hotels: prev.hotels.map((h) =>
          h.id === id ? { ...h, ...updates } : h
        ),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const deleteHotel = useCallback((id: string) => {
    setData((prev) => {
      const updated = {
        ...prev,
        hotels: prev.hotels.filter((h) => h.id !== id),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  // Buses
  const addBus = useCallback((bus: Omit<Bus, 'id'>) => {
    const newBus: Bus = { ...bus, id: generateId() };
    setData((prev) => {
      const updated = {
        ...prev,
        buses: [...prev.buses, newBus],
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const updateBus = useCallback((id: string, updates: Partial<Bus>) => {
    setData((prev) => {
      const updated = {
        ...prev,
        buses: prev.buses.map((b) =>
          b.id === id ? { ...b, ...updates } : b
        ),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const deleteBus = useCallback((id: string) => {
    setData((prev) => {
      const updated = {
        ...prev,
        buses: prev.buses.filter((b) => b.id !== id),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  // Testimonials
  const addTestimonial = useCallback((testimonial: Omit<Testimonial, 'id'>) => {
    const newTestimonial: Testimonial = { ...testimonial, id: generateId() };
    setData((prev) => {
      const updated = {
        ...prev,
        testimonials: [...prev.testimonials, newTestimonial],
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const updateTestimonial = useCallback((id: string, updates: Partial<Testimonial>) => {
    setData((prev) => {
      const updated = {
        ...prev,
        testimonials: prev.testimonials.map((t) =>
          t.id === id ? { ...t, ...updates } : t
        ),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  const deleteTestimonial = useCallback((id: string) => {
    setData((prev) => {
      const updated = {
        ...prev,
        testimonials: prev.testimonials.filter((t) => t.id !== id),
      };
      saveData(updated);
      return updated;
    });
  }, []);

  // Settings
  const updateSettings = useCallback((updates: Partial<SiteSettings>) => {
    setData((prev) => {
      const updated = {
        ...prev,
        settings: { ...prev.settings, ...updates },
      };
      saveData(updated);
      return updated;
    });
  }, []);

  // Export/Import
  const exportData = useCallback((): string => {
    return JSON.stringify(data, null, 2);
  }, [data]);

  const importData = useCallback((jsonString: string): boolean => {
    try {
      const imported = JSON.parse(jsonString) as CMSData;
      setData(imported);
      saveData(imported);
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }, []);

  const resetData = useCallback(() => {
    const freshData: CMSData = {
      packages: defaultPackages,
      hotels: defaultHotels,
      buses: defaultBuses,
      testimonials: defaultTestimonials,
      settings: defaultSettings,
      lastUpdated: new Date().toISOString(),
      version: 1,
    };
    setData(freshData);
    saveData(freshData);
  }, []);

  return {
    data,
    isLoading,
    // Packages
    addPackage,
    updatePackage,
    deletePackage,
    // Hotels
    addHotel,
    updateHotel,
    deleteHotel,
    // Buses
    addBus,
    updateBus,
    deleteBus,
    // Testimonials
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    // Settings
    updateSettings,
    // Utils
    exportData,
    importData,
    resetData,
    updateData,
  };
};

// Password management
export const useCMSAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem(CMS_AUTH_KEY) === 'true';
  });

  const setPassword = useCallback((password: string) => {
    localStorage.setItem(CMS_PASSWORD_KEY, password);
  }, []);

  const login = useCallback((password: string): boolean => {
    const storedPassword = localStorage.getItem(CMS_PASSWORD_KEY);
    if (!storedPassword || password === storedPassword) {
      setIsAuthenticated(true);
      localStorage.setItem(CMS_AUTH_KEY, 'true');
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    localStorage.removeItem(CMS_AUTH_KEY);
  }, []);

  const hasPassword = useCallback((): boolean => {
    return !!localStorage.getItem(CMS_PASSWORD_KEY);
  }, []);

  return {
    isAuthenticated,
    setPassword,
    login,
    logout,
    hasPassword,
  };
};

export default useCMS;